import type {
  AppSettings,
  Mission,
  MissionOpening,
  MissionSnapshot,
  PlayerMessage,
  UnlockMethod,
} from './types.js';
import type { AiReviewState, SolutionConfig, SolutionMode, SolutionStatus } from './solution.js';
import type { NotifyTemplates } from './push.js';

/**
 * תצוגה מקדימה של תנאי הפתיחה בלבד — ללא תוכן נרטיבי (משימה/הכנות/יוטיוב/קישור מיקום גולמי).
 * נשלחת לשחקן כדי לאפשר הערכת פתיחה מקומית (כולל Offline) מבלי לחשוף תוכן עתידי.
 */
export interface NextPendingDTO {
  id: string;
  missionNumber: number | null;
  date: string;
  unlockTimeRaw: string;
  manualRaw: string;
  manuallyUnlockedAt: string | null;
  hasLocationCondition: boolean;
  /** האם יש בכלל תוכן למשימה (עמודת "המשימה" לא ריקה) — משימה ריקה לעולם לא נפתחת לשחקן */
  hasContent: boolean;
  /** המנהל איפס את הפתיחה שלה — לא נפתחת לפי זמן עד שישונו התאריך/השעה (אפשור ידני/מיקום כן פותחים) */
  autoOpenHeld: boolean;
  /** המשימה הקודמת דורשת פתרון שעוד לא אושר — לא נפתחת לפי זמן/מיקום עד שהפתרון יאושר */
  gatedBySolution: boolean;
  resolvedLat: number | null;
  resolvedLng: number | null;
  version: number;
}

/** בונה אובייקט Mission תקני מתוך NextPendingDTO, לשימוש ישיר ב-evaluateMission המשותף */
export function stubMissionFromPreview(dto: NextPendingDTO): Mission {
  return {
    id: dto.id,
    missionNumber: dto.missionNumber,
    hour: '',
    day: '',
    date: dto.date,
    locationUrl: dto.hasLocationCondition ? '(מוסתר עד לפתיחה)' : '',
    unlockTimeRaw: dto.unlockTimeRaw,
    manualRaw: dto.manualRaw,
    task: '',
    preparations: '',
    youtubeUrl: '',
    imageUrl: '',
    requiredSolution: '',
    resolvedLat: dto.resolvedLat,
    resolvedLng: dto.resolvedLng,
    manuallyUnlockedAt: dto.manuallyUnlockedAt,
    version: dto.version,
    updatedAt: '',
    deletedAt: null,
    sortIndex: 0,
  };
}

/** הגשת פתרון כפי שהשחקן רואה אותה */
export interface PlayerSubmissionDTO {
  id: string;
  text: string;
  imageUrl: string | null;
  submittedAt: string;
  status: SolutionStatus;
  /** הערת המנהל (סיבת דחייה / הכוונה); ריק אם אין */
  note: string;
  reviewedAt: string | null;
  /** התמונה נבדקת כרגע בבדיקה האוטומטית — התשובה תגיע בעוד כמה שניות */
  checking: boolean;
}

/** מצב הפתרון הנדרש במשימה הנוכחית של השחקן */
export interface PlayerSolutionDTO {
  missionId: string;
  missionNumber: number | null;
  mode: SolutionMode;
  prompt: string;
  /** יש תשובות מוגדרות שנבדקות אוטומטית (התשובות עצמן לא נחשפות לשחקן) */
  autoCheck: boolean;
  /** תמונה שמוגשת כאן נבדקת אוטומטית (והתשובה מגיעה בעוד כמה שניות), בלי להמתין למנהל */
  aiCheck: boolean;
  approved: boolean;
  /** מהחדשה לישנה, עד 10 */
  submissions: PlayerSubmissionDTO[];
}

export interface PlayerStateDTO {
  serverTime: string;
  current: MissionOpening | null;
  nextPending: NextPendingDTO | null;
  /** הפתרון הנדרש במשימה הנוכחית; null = המשימה הנוכחית לא דורשת פתרון */
  solution: PlayerSolutionDTO | null;
  history: MissionOpening[];
  /** הודעות שהשחקן שלח (50 אחרונות, מהחדשה לישנה) — לתצוגת "נשלח" בטרמינל */
  messages: PlayerMessage[];
  settings: Pick<
    AppSettings,
    | 'syncIntervalMs'
    | 'gpsRadiusMeters'
    | 'autoOpenByTime'
    | 'soundEnabled'
    | 'animationsEnabled'
    | 'operationName'
    | 'timeZone'
    | 'testMode'
  >;
  totalMissions: number;
  online: true;
}

export interface OpenMissionRequest {
  missionId: string;
  method: UnlockMethod;
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  clientEventId: string;
  isTest: boolean;
}

export type OpenMissionResult =
  | { ok: true; opening: MissionOpening }
  | { ok: false; code: 'not_pending'; reason: string }
  | { ok: false; code: 'condition_not_met'; reason: string }
  | { ok: false; code: 'invalid'; reason: string }
  | { ok: false; code: 'not_found' };

export interface AdminMissionListDTO {
  missions: (Mission & {
    state: 'locked' | 'available' | 'opened';
    autoOpenHeld?: boolean;
    /** הגדרת פתרון נדרש למשימה (null/חסר = אין) */
    solution?: SolutionConfig | null;
    /** כמה הגשות פתרון ממתינות לבדיקה במשימה הזו */
    solutionPending?: number;
  })[];
  /** הגדרת "פתיחה אוטומטית לפי זמן" הנוכחית — כדי שהרשימה תסביר למה משימה שהגיע זמנה לא נפתחה */
  autoOpenByTime?: boolean;
  /** אתחול כל המשימות האחרון שעדיין ניתן לבטל; null = אין */
  resetUndo?: ResetUndoDTO | null;
}

/** אתחול כל המשימות שניתן עדיין לבטל */
export interface ResetUndoDTO {
  at: string;
  /** כמה פתיחות אופסו */
  openings: number;
  /** עד מתי ניתן לבטל (ISO) */
  expiresAt: string;
}

export type ResetAllResult =
  | { ok: true; mode: 'real' | 'test'; openings: number; undo: ResetUndoDTO | null }
  | { ok: false; error: string };

export type UndoResetAllResult =
  | { ok: true; openings: number }
  | { ok: false; code: 'nothing_to_undo' | 'expired' | 'conflict'; error: string };

/** בקשת הגשת פתרון מהשחקן. id הוא מזהה אירוע מהלקוח (אידמפוטנטי) */
export interface SubmitSolutionRequest {
  id: string;
  missionId: string;
  text: string;
  /** נתיב תמונה שהועלתה (/uploads/...) או null */
  imageUrl: string | null;
}

export type SubmitSolutionResult =
  | { ok: true; submission: PlayerSubmissionDTO; opened: boolean }
  | {
      ok: false;
      code: 'invalid' | 'not_found' | 'not_required' | 'not_opened' | 'already_approved' | 'rate_limited' | 'busy';
      reason: string;
    };

/** הגשת פתרון כפי שהמנהל רואה אותה */
export interface AdminSubmissionDTO {
  id: string;
  missionId: string;
  missionNumber: number | null;
  text: string;
  imageUrl: string | null;
  submittedAt: string;
  status: SolutionStatus;
  note: string;
  reviewedAt: string | null;
  /** נבדקה אוטומטית (התאמה/אי-התאמה לתשובה מוגדרת) */
  autoReviewed: boolean;
  /** בוטלה כי הפתיחה של המשימה אופסה */
  voided: boolean;
  isTest: boolean;
  /** בדיקת התמונה האוטומטית (Claude): מצבה, ומה שהיא ראתה */
  aiState: AiReviewState;
  aiNote: string;
}

export interface AdminSubmissionsDTO {
  submissions: AdminSubmissionDTO[];
  /** כמה הגשות ממתינות לבדיקה (במצב הנוכחי: אמיתי/בדיקה) */
  pendingCount: number;
}

export type ReviewSolutionResult =
  | { ok: true; submission: AdminSubmissionDTO; opened: boolean }
  | { ok: false; error: string };

/** בקשת שליחת הודעה מהשחקן. id הוא מזהה אירוע מהלקוח (אידמפוטנטי, כמו clientEventId בפתיחת משימה) */
export interface SendMessageRequest {
  id: string;
  text: string;
  imageUrl: string | null;
  missionId: string | null;
  isTest: boolean;
}

export type SendMessageResult =
  | { ok: true; message: PlayerMessage }
  | { ok: false; code: 'invalid'; reason: string };

export interface AdminMessagesDTO {
  messages: PlayerMessage[];
}

export interface AdminSendMessageRequest {
  text: string;
  /** מה לכתוב בגוף ההתראה במקום התבנית (למשל כדי לא לחשוף את תוכן ההודעה על מסך נעול) */
  notifyText?: string;
  /** דחיפות לאותה הודעה; לא נשלח = לפי התבנית בהגדרות */
  urgent?: boolean;
  timeSensitive?: boolean;
  /** שלח הודעה בלי התראה */
  silent?: boolean;
}

export type AdminSendMessageResult =
  | { ok: true; message: PlayerMessage }
  | { ok: false; error: string };

/** אזור הזמן שהוגדר + אזורי הזמן שמכשירי השחקנים דיווחו עליהם לאחרונה */
export interface TimeZoneInfoDTO {
  configured: string;
  players: { tz: string; lastSeenAt: string; offsetMinutes: number }[];
}

/** ניסוח ההתראות לשחקנים: התבניות הנוכחיות וברירות המחדל (לכפתור "שחזר ברירת מחדל") */
export interface NotifyTemplatesDTO {
  templates: NotifyTemplates;
  defaults: NotifyTemplates;
}

export interface SheetsStatusDTO {
  configured: boolean;
  running: boolean;
  lastAt: string | null;
  lastOk: boolean | null;
  lastMessage: string;
  conflicts: number;
  pendingDirty: number;
  /** בעיות בגיליון שהסנכרון הצליח למרות זאת (כפילות מספרים, שורות בלי מספר) */
  warnings: string[];
  /** הסנכרון האחרון נעצר ע"י רשת הביטחון (גיליון ריק / חסרות רוב המשימות) וניתן לאשר ידנית */
  needsForce: boolean;
  /** גרסת סקריפט ה-Apps Script המחובר (null = לא ידוע); 2 ומעלה מעבירה עיצוב טקסט מהגיליון */
  scriptVersion: number | null;
}

export interface SyncPushResultItem {
  opId: string;
  ok: boolean;
  error?: string;
  conflictServerVersion?: number;
}

export interface SyncPushResponse {
  results: SyncPushResultItem[];
}

export type MissionSnapshotDTO = MissionSnapshot;
